/**
 * Helper functions for managing Quill editor instances
 */

// Store active editor instances
const editorInstances = new Map();

const INFO_ELEMENT_DESCRIPTION_SELECTOR = '#info-element-description1';

const quillRootConnected = (editor) => !!(editor && editor.root && editor.root.isConnected);

// Quill's toolbar is a sibling inserted before the container. Clearing the
// container (or constructing Quill twice) leaves that toolbar behind and drops
// the contenteditable, which is the missing description field.
const detachQuillChrome = (element) => {
  if (!element) return;

  let sibling = element.previousElementSibling;
  while (sibling && sibling.classList && sibling.classList.contains('ql-toolbar')) {
    const prev = sibling.previousElementSibling;
    sibling.remove();
    sibling = prev;
  }

  element.innerHTML = '';
  element.classList.remove('ql-container', 'ql-snow', 'ql-bubble', 'ql-disabled');
  if (element.__quill) {
    delete element.__quill;
  }
};

/**
 * Initialize a Quill editor
 * @param {string} selector - CSS selector for the editor container
 * @param {Object} options - Additional options for the editor
 * @returns {Quill} The Quill editor instance
 */
export const initQuillEditor = (selector, options = {}) => {
  const existing = editorInstances.get(selector);
  if (quillRootConnected(existing)) {
    return existing;
  }

  const element = (existing && existing.container) || document.querySelector(selector);
  if (existing || (element && element.classList.contains('ql-container') && !element.querySelector('.ql-editor'))) {
    detachQuillChrome(element);
    editorInstances.delete(selector);
  }

  // Default configuration
  const defaultOptions = {
    theme: 'snow',
    modules: {
      toolbar: [
        //place all possible toolbar options here
        ['bold', 'italic', 'underline', 'strike'],
        ['blockquote', 'code-block'], ['link'],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'align': ['left', 'center', 'right', 'justify'] }],
        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
        ['video'],
        ['clean']
      ],
      table: true
    },
    placeholder: 'Start writing...',
    readOnly: false
  };

  // Merge default options with provided options
  const config = { ...defaultOptions, ...options };

  // Create editor instance
  const editor = new Quill(selector, config);

  // Store the instance
  editorInstances.set(selector, editor);

  return editor;
};

/**
 * Shared description editor for the info-element modal (add and edit).
 * Reuses a live instance so consecutive opens do not stack toolbars.
 */
export const initInfoElementDescriptionEditor = () => initQuillEditor(INFO_ELEMENT_DESCRIPTION_SELECTOR, {
  modules: {
    toolbar: [
      ['bold', 'italic', 'underline', 'link'],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      ['clean']
    ]
  },
  placeholder: 'Enter description...'
});

/**
 * Get a Quill editor instance
 * @param {string} selector - CSS selector used to initialize the editor
 * @returns {Quill|null} The Quill editor instance or null if not found
 */
export const getQuillEditor = (selector) => {
  return editorInstances.get(selector) || null;
};

/**
 * Remove a Quill editor instance
 * @param {string} selector - CSS selector used to initialize the editor
 */
export const removeQuillEditor = (selector) => {
  const editor = editorInstances.get(selector);
  const element = (editor && editor.container) || document.querySelector(selector);
  if (editor || element) {
    detachQuillChrome(element);
  }
  editorInstances.delete(selector);
};

/**
 * Set content for a Quill editor
 * @param {string} selector - CSS selector used to initialize the editor
 * @param {string} content - HTML content to set
 */
export const setQuillContent = (selector, content) => {
  const editor = editorInstances.get(selector);
  if (!quillRootConnected(editor)) {
    return;
  }

  const html = content == null ? '' : String(content);
  const normalized = html.trim();
  const isEmpty = normalized === '' || normalized === '<p><br></p>' || normalized === '<p></p>';

  // Assigning root.innerHTML detaches Quill's document and can collapse the
  // editing surface, leaving only the toolbar. Use the editor API instead.
  if (isEmpty) {
    editor.setText('');
    return;
  }

  try {
    editor.clipboard.dangerouslyPasteHTML(normalized);
  } catch (err) {
    editor.setText('');
  }
};

/**
 * Get content from a Quill editor
 * @param {string} selector - CSS selector used to initialize the editor
 * @returns {string} The HTML content
 */
export const getQuillContent = (selector) => {
  const editor = editorInstances.get(selector);
  return editor ? editor.root.innerHTML : '';
};

/**
 * Initialize a simple Quill editor (for product descriptions)
 * @param {string} selector - CSS selector for the editor container
 * @returns {Quill} The Quill editor instance
 */
export const initSimpleQuillEditor = (selector) => {
  return initQuillEditor(selector, {
    modules: {
      toolbar: [
        ['bold', 'italic', 'underline'],
        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
        ['clean']
      ]
    },
    placeholder: 'Enter product description...'
  });
}; 